// apps/client/src/routes/guards/RequireLeadTab.tsx
//
// Per-route guard for the Lead Capture subapp, reading the SAME map as
// LeadCaptureTopNav (config/leadTabAccess.ts).
//
// Before this, every /leads/* config route sat under one RequireRole that
// admitted the whole broker family and service_staff, so the admin-only tabs
// were hidden in the nav but not actually guarded: typing the URL reached the
// page, which then failed at the callable. Hiding is not access control.
//
// This guard cannot widen anything: a route still has to pass the parent
// RequireRole and the lead_capture FeatureGate first, and the callables
// remain the real authority.

import React from 'react';
import { useOutletContext, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCapability } from '../../hooks/useCapability';
import { LEAD_TAB_ACCESS, canAccessLeadTab } from '../../config/leadTabAccess';

interface AppOutletContext {
  userData?: { role?: string };
}

/**
 * `path` pins which entry in the access map applies. It is passed explicitly
 * rather than derived from the URL so a nested or parameterised route (the DM
 * thread view at /leads/dm-inbox/:id) resolves to its parent tab.
 */
interface RequireLeadTabProps {
  path: string;
}

const RequireLeadTab: React.FC<RequireLeadTabProps> = ({ path }) => {
  const outletContext = useOutletContext<AppOutletContext>();
  const location = useLocation();
  const { claims } = useAuth();

  const role = outletContext?.userData?.role ?? (claims?.role as string | undefined);
  const allowedLeadPages = Array.isArray(claims?.allowedLeadPages)
    ? new Set<string>(claims!.allowedLeadPages as string[])
    : null;

  // Hooks must run unconditionally, so the capability is always resolved even
  // for tabs that do not use one.
  const dmInboxAllowed = useCapability(
    'access_conversations_inbox',
    LEAD_TAB_ACCESS['/leads/dm-inbox'].roles,
  );
  const hasCapability = React.useCallback(
    (capability: string) =>
      capability === 'access_conversations_inbox' ? dmInboxAllowed : false,
    [dmInboxAllowed],
  );

  if (!role) return <Navigate to="/" replace />;

  if (!canAccessLeadTab(path, role, allowedLeadPages, hasCapability)) {
    // Send them to the one lead surface every role can reach, rather than out
    // of the subapp entirely. `replace` keeps the denied URL out of history.
    // Guard against a redirect loop if the inbox itself were ever denied.
    if (location.pathname.startsWith('/leads/inbox')) {
      return <Navigate to="/" replace />;
    }
    return <Navigate to="/leads/inbox" replace />;
  }

  return <Outlet context={outletContext} />;
};

export default RequireLeadTab;
